import { useContext, useState } from 'react';
import { AdminTableContext } from '../../AdminTable';
import { useQueryClient } from '@tanstack/react-query';
import { AdminProfessionals } from '@platform/client';
import { InputDropdownItem, FormsInputDropdown, FormsCheckBox, Alert } from '@platform/components';
import { ProVettingResult } from '@platform/client/schemas';
import { useForm, SubmitHandler } from 'react-hook-form';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';

type ProVettingFormData = {
  vettingResult: ProVettingResult | null;
  sendEmail: boolean;
};

export const SingleProVettingModal = (props: {
  onClose: () => void;
  userId: number;
  currentVettingResult: ProVettingResult;
  proName: string;
}) => {
  return (
    <BaseVettingModal
      onClose={props.onClose}
      isBatch={false}
      userId={props.userId}
      currentVettingResult={props.currentVettingResult}
      proName={props.proName}
    />
  );
};

export const BatchProVettingModal = (props: { onClose: () => void }) => {
  return <BaseVettingModal onClose={props.onClose} isBatch={true} />;
};

const BaseVettingModal = (
  props: { onClose: () => void } & (
    | {
        isBatch: true;
        userId?: number;
        currentVettingResult?: ProVettingResult;
        proName?: string;
      }
    | { isBatch: false; userId: number; currentVettingResult: ProVettingResult; proName: string }
  ),
) => {
  const { isBatch, userId } = props;
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { rowSelection, adminQueryOptionsV2 } = useContext(AdminTableContext);

  const resultItems: InputDropdownItem[] = Object.values(ProVettingResult).map((result) => ({
    textValue: result,
    text: result.split('_').join(' '),
  }));

  const { control, watch, formState, handleSubmit } = useForm<ProVettingFormData>({
    defaultValues: {
      vettingResult: null,
      sendEmail: false,
    },
  });
  const currentResult = watch('vettingResult');

  const queryClient = useQueryClient();
  const mutateOptions = {
    onMutate: () => {
      setModalStage('submitting');
    },
    onError: () => {
      setModalStage('error');
    },
    onSuccess: () => {
      setModalStage('success');
      queryClient.invalidateQueries({
        queryKey: [
          ...AdminProfessionals.getGetAdminProfessionalsQueryKey({
            ...adminQueryOptionsV2,
          }),
        ],
      });
    },
  };
  const singleVettingMutator = AdminProfessionals.useUpdateProVetting({
    mutation: {
      ...mutateOptions,
    },
  });
  const batchVettingMutator = AdminProfessionals.useUpdateProVettingBatch({
    mutation: {
      ...mutateOptions,
    },
  });

  const onSubmit: SubmitHandler<ProVettingFormData> = (data) => {
    if (data.vettingResult) {
      if (isBatch) {
        const selectedUserIds = Object.keys(rowSelection ?? {}).map((key) => Number(key));
        batchVettingMutator.mutate({
          data: {
            userIds: selectedUserIds,
            vettingResult: data.vettingResult,
            sendEmail: data.sendEmail,
          },
        });
      } else {
        singleVettingMutator.mutate({
          data: {
            userId: userId,
            vettingResult: data.vettingResult,
            sendEmail: data.sendEmail,
          },
        });
      }
    }
  };

  const includeNotify =
    currentResult === 'Accepted' || currentResult === 'Rejected' || currentResult === 'Waitlist';

  return (
    <AdminFormModal
      onClose={props.onClose}
      title={
        isBatch ?
          `Batch Vetting: ${Object.keys(rowSelection ?? {}).length} records`
        : `Vetting Update: ${props.proName}`
      }
      modalStage={modalStage}
      submitCTA={{
        text: 'Save Vetting',
        callback: () => handleSubmit(onSubmit)(),
        buttonVariant: 'primary',
      }}
    >
      <div className='flex flex-col md:w-[420px]'>
        {!isBatch && (
          <>
            <p className='mb-4'>
              <span className='font-semibold'>Current result:</span>{' '}
              {props.currentVettingResult.replaceAll('_', ' ')}
            </p>
          </>
        )}

        <FormsInputDropdown
          type='default'
          inputDisabled
          control={control}
          name='vettingResult'
          label='New vetting result'
          items={resultItems}
          placeholder='Select a result'
          rules={{ required: 'Vetting result is required' }}
          error={formState.errors.vettingResult}
        />
        {includeNotify && (
          <div>
            <FormsCheckBox
              control={control}
              name='sendEmail'
              description='Notify professional'
              size='md'
            />
          </div>
        )}
        {currentResult === 'Disabled' && (
          <Alert variant='warning'>
            Pending match activity will be cancelled, cohort memberships will be dropped, and
            dashboard access will be blocked.
          </Alert>
        )}
      </div>
    </AdminFormModal>
  );
};
